// ─────────────────────────────────────────────────────────────────────────────
// Camada de FORMATAÇÃO — números, moeda, datas e tempo relativo. Uma só fonte
// para a app lean E a consola HQ. Antes: `const kz = n => n.toLocaleString('pt-AO')`
// e `fmtDate`/`fmtDateTime`/`dt` copiados em ~20 ficheiros (lean pt-AO, HQ pt-PT
// com um `.replace(/ /g,'.')` que NEM funcionava — o separador é um espaço fino
// U+202F, não U+0020). Output idêntico nas duas → unificar não muda nada no ecrã.
//
// Locale canónico: pt-AO (locale do PRODUTO; para PT-EU o resultado é igual).
// ─────────────────────────────────────────────────────────────────────────────

export const LOCALE = 'pt-AO';

// ── Idioma ativo ─────────────────────────────────────────────────────────────
// A app (lean ou HQ) chama `setFormatLocale` quando o idioma muda; todos os
// formatadores abaixo passam a seguir esse locale SEM mudar nenhum ponto de
// chamada. `tr` traduz as poucas palavras que o formato contém ('Grátis',
// 'agora', 'há 5 min'); sem `tr`, ficam as palavras pt de origem.
type Tr = (key: string, opts?: Record<string, unknown>) => string;
let activeLocale = LOCALE;
let tr: Tr | null = null;
export function setFormatLocale(tag: string, translate?: Tr) { activeLocale = tag; tr = translate ?? null; }
export const getFormatLocale = () => activeLocale;
const word = (key: string, fallback: string, opts?: Record<string, unknown>) => (tr ? tr(key, opts) : fallback);

function toDate(s?: string | number | Date | null): Date | null {
  if (s === null || s === undefined || s === '') return null;
  const d = s instanceof Date ? s : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

// ── Números & moeda ──────────────────────────────────────────────────────────
/** Milhares agrupados: 1234567 → "1 234 567". */
export function formatNumber(n?: number | string | null): string {
  const v = Number(n ?? 0);
  return Number.isFinite(v) ? v.toLocaleString(activeLocale) : '0';
}

/** Dinheiro: 12000 → "12 000 Kz"; 0/negativo → rótulo grátis. Moeda parametrizável
 *  (Kz/AOA no produto, MXC na carteira). */
export function formatMoney(
  n?: number | string | null,
  currency = 'Kz',
  opts: { freeLabel?: string } = {},
): string {
  const v = Number(n ?? 0);
  return v > 0 ? `${v.toLocaleString(activeLocale)} ${currency}` : (opts.freeLabel ?? word('fmt.free', 'Grátis'));
}

// ── Datas ────────────────────────────────────────────────────────────────────
/** Cartões: "ter, 21/07" (dia da semana + dia + mês curto). */
export function formatDay(s?: string | null): string {
  const d = toDate(s);
  return d ? d.toLocaleDateString(activeLocale, { weekday: 'short', day: '2-digit', month: 'short' }) : '';
}
/** Detalhe: "terça, 21 de julho" (mês por extenso). */
export function formatDayLong(s?: string | null): string {
  const d = toDate(s);
  return d ? d.toLocaleDateString(activeLocale, { weekday: 'short', day: '2-digit', month: 'long' }) : '';
}
/** Curto sem ano: "21/07". */
export function formatDayShort(s?: string | null): string {
  const d = toDate(s);
  return d ? d.toLocaleDateString(activeLocale, { day: '2-digit', month: 'short' }) : '';
}
/** Numérico com ano: "21/07/2026". */
export function formatDateNumeric(s?: string | null): string {
  const d = toDate(s);
  return d ? d.toLocaleDateString(activeLocale, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
}
/** Só horas: "15:30". */
export function formatTime(s?: string | null): string {
  const d = toDate(s);
  return d ? d.toLocaleTimeString(activeLocale, { hour: '2-digit', minute: '2-digit' }) : '';
}
/** Rastreio (registos HQ): "21/07/2026, 15:30". */
export function formatDateTime(s?: string | null): string {
  const d = toDate(s);
  return d ? d.toLocaleString(activeLocale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}
/** Rastreio com SEGUNDOS (auditoria): "21/07/2026, 15:30:45". */
export function formatDateTimeSec(s?: string | null): string {
  const d = toDate(s);
  return d ? d.toLocaleString(activeLocale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
}
/** Data + intervalo de horas: "ter, 21/07 · 15:30 – 17:00". Coerente com os PDFs. */
export function formatDateRange(start?: string | null, end?: string | null): string {
  const day = formatDay(start);
  if (!day) return '';
  const a = formatTime(start);
  const b = formatTime(end);
  const range = a && b ? `${a} – ${b}` : a;
  return range ? `${day} · ${range}` : day;
}

// ── Tempo relativo ───────────────────────────────────────────────────────────
/** "agora", "há 5 min", "há 3 h", "há 2 d", ou a data para intervalos maiores. */
export function timeAgo(s?: string | null): string {
  const d = toDate(s);
  if (!d) return '';
  const sec = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (sec < 45) return word('fmt.now', 'agora');
  if (sec < 3600) return word('fmt.agoMin', `há ${Math.round(sec / 60)} min`, { n: Math.round(sec / 60) });
  if (sec < 86400) return word('fmt.agoHour', `há ${Math.round(sec / 3600)} h`, { n: Math.round(sec / 3600) });
  if (sec < 604800) return word('fmt.agoDay', `há ${Math.round(sec / 86400)} d`, { n: Math.round(sec / 86400) });
  return formatDayShort(s);
}

/**
 * Tempo relativo COMPACTO: "agora", "5m", "3h", "2d".
 *
 * Estava escrito à mão dentro do detalhe do evento (`fmtAgo`), com o argumento
 * de que a intenção é diferente do `timeAgo` acima — e é. Mas ser uma intenção
 * diferente não o torna um caso particular de UM ecrã: é o carimbo de qualquer
 * linha de conversa (comentários, mensagens do Tap). Aqui é uma escolha do
 * sistema; lá era uma cópia à espera de divergir.
 */
export function timeAgoShort(s?: string | null): string {
  const d = toDate(s);
  if (!d) return '';
  const sec = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (sec < 60) return word('fmt.now', 'agora');
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d`;
}

/** Plural: `plural(1, 'organização', 'organizações')` → «1 organização». Evita «1 eventos».
 *  As palavras já vêm traduzidas por quem chama; a regra "1 = singular" vale em pt/en/fr/es. */
export const plural = (n: number, um: string, varios: string): string => `${n} ${n === 1 ? um : varios}`;
